import AllInclusiveIcon from "@mui/icons-material/AllInclusive";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import LocalFireDepartmentIcon from "@mui/icons-material/LocalFireDepartment";
import MarkEmailReadIcon from "@mui/icons-material/MarkEmailRead";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import { Box, ButtonBase, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { BaseModal } from "@/components/ui/BaseModal";
import SubscribeForm from "@/components/mailing/SubscribeForm";
import type { SubscribeOutcome } from "@/utils/mailingUnlock";
import { FREE_RUNS } from "@/utils/arcade/playGate";
import { getArcadeBackend } from "./arcadeBackend";
import { BranchesSignInButton } from "./BranchesSignInButton";
import type { ArcadeRetention, GateWhy } from "./useArcadeRetention";

const COPY: Record<GateWhy, { title: string; lede: string }> = {
  "free-runs-used": {
    title: "Unlock the arcade",
    lede: `That was your ${FREE_RUNS} free plays. Drop your email and keep going, every song, every game, as long as you like.`,
  },
  "members-only": {
    title: "Endless run is for members",
    lede: "One run through the whole catalog, song after song. Drop your email and it opens now.",
  },
  "save-score": {
    title: "Save your score",
    lede: "Put your name on the leaderboard and keep your best runs. Drop your email and it is saved.",
  },
};

const PERKS = [
  { icon: AllInclusiveIcon, text: "Unlimited plays on every song" },
  { icon: EmojiEventsIcon, text: "Your name on the leaderboards" },
  { icon: CalendarTodayIcon, text: "A new daily challenge every day" },
  { icon: LocalFireDepartmentIcon, text: "Streaks and badges that stick" },
];

const text = { fontFamily: "Poppins, sans-serif", color: "var(--logoColor2)" } as const;

export function UnlockArcadeDialog({ retention }: { retention: ArcadeRetention }) {
  const { dialog } = retention;
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [confirmed, setConfirmed] = useState(false);

  useEffect(() => {
    if (dialog.open) {
      setSentTo(null);
      setConfirmed(false);
    }
  }, [dialog.open]);

  const copy = COPY[dialog.why];

  const onSubscribed = (outcome: SubscribeOutcome) => {
    setSentTo(outcome.email);
    setConfirmed(outcome.status === "confirmed");
    retention.unlockedByEmail();
  };

  const subscribeWith = async (request: { email: string; pagePath?: string; referrer?: string; visitorId?: string; country?: string; deviceType?: string; website?: string }) =>
    getArcadeBackend().subscribe({ ...request, source: "arcade_gate" });

  return (
    <BaseModal
      isOpen={dialog.open}
      onClose={retention.closeDialog}
      desktopModalMobileDrawer
      drawerBottomOffset="0px"
      drawerMaxHeight="calc(100dvh - 4rem)"
      bodySx={{
        backgroundColor: "var(--color5)",
        backgroundImage: "linear-gradient(180deg, color-mix(in srgb, var(--logoColor) 10%, transparent), transparent 45%)",
        border: "1px solid color-mix(in srgb, var(--logoColor) 20%, transparent)",
        borderRadius: { xs: "20px 20px 0 0", md: "22px" },
        boxShadow: "0 30px 80px -20px rgba(0,0,0,0.7)",
        width: { md: "min(460px, calc(100vw - 32px))" },
        color: "var(--logoColor2)",
      }}
    >
      <Box sx={{ display: "grid", gap: { xs: "14px", md: "16px" }, p: { xs: "4px 18px calc(18px + env(safe-area-inset-bottom, 0px))", md: "28px 28px 26px" } }}>
        {sentTo ? (
          <>
            <Box sx={{ display: "flex", alignItems: "center", gap: "12px", pr: "40px" }}>
              <Box sx={{ width: 40, height: 40, borderRadius: "999px", display: "grid", placeItems: "center", flexShrink: 0, color: "var(--logoColor)", backgroundColor: "color-mix(in srgb, var(--logoColor) 16%, transparent)" }}>
                <MarkEmailReadIcon sx={{ fontSize: 21 }} />
              </Box>
              <Typography component="h2" sx={{ ...text, m: 0, fontFamily: "Hanson", fontWeight: 700, fontSize: "22px", lineHeight: 1.15 }}>
                You are in
              </Typography>
            </Box>
            <Typography sx={{ ...text, fontSize: "15px", lineHeight: 1.6, opacity: 0.9 }}>
              {confirmed
                ? "Play as much as you like. Your email is already confirmed."
                : `Play as much as you like. We sent a link to ${sentTo}. Click it and your scores go on the leaderboard.`}
            </Typography>
            <ButtonBase
              onClick={retention.playNow}
              autoFocus
              sx={{ minHeight: "52px", borderRadius: "999px", gap: "8px", fontFamily: "Poppins, sans-serif", fontWeight: 700, fontSize: "16px", color: "var(--color5)", backgroundColor: "var(--logoColor2)", "&:focus-visible": { outline: "2px solid var(--logoColor)", outlineOffset: "3px" } }}
            >
              <PlayArrowIcon sx={{ fontSize: 22 }} />
              {dialog.resumable ? "Play now" : "Back to the game"}
            </ButtonBase>
          </>
        ) : (
          <>
            <Box sx={{ pr: "40px" }}>
              <Typography sx={{ ...text, fontSize: "12px", fontWeight: 700, letterSpacing: "0.16em", textTransform: "uppercase", color: "var(--logoColor)" }}>Free with your email</Typography>
              <Typography id="arcade-unlock-title" component="h2" sx={{ ...text, m: "4px 0 0", fontFamily: "Hanson", fontWeight: 700, fontSize: { xs: "24px", md: "26px" }, lineHeight: 1.1 }}>
                {copy.title}
              </Typography>
            </Box>
            <Typography sx={{ ...text, fontSize: "15px", lineHeight: 1.55, opacity: 0.9 }}>{copy.lede}</Typography>
            <Box component="ul" sx={{ listStyle: "none", m: 0, p: 0, display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px" }}>
              {PERKS.map(({ icon: Icon, text: perk }) => (
                <Box component="li" key={perk} sx={{ display: "flex", alignItems: "center", gap: { xs: "8px", md: "10px" }, p: { xs: "8px", md: "8px 10px" }, borderRadius: "12px", backgroundColor: "color-mix(in srgb, var(--logoColor) 8%, transparent)" }}>
                  <Icon sx={{ fontSize: 18, color: "var(--logoColor)", flexShrink: 0 }} />
                  <Typography sx={{ ...text, fontSize: "13px", lineHeight: 1.35 }}>{perk}</Typography>
                </Box>
              ))}
            </Box>
            <SubscribeForm
              source="arcade_gate"
              buttonLabel="Unlock"
              compact
              accentColor="var(--logoColor2)"
              inkColor="var(--color5)"
              onSubscribed={onSubscribed}
              subscribeWith={subscribeWith}
            />
            <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
              <Box sx={{ flex: 1, height: "1px", backgroundColor: "color-mix(in srgb, var(--logoColor) 22%, transparent)" }} />
              <Typography sx={{ ...text, fontSize: "12px", opacity: 0.7 }}>or</Typography>
              <Box sx={{ flex: 1, height: "1px", backgroundColor: "color-mix(in srgb, var(--logoColor) 22%, transparent)" }} />
            </Box>
            <BranchesSignInButton
              onBeforeOpen={retention.closeDialog}
              sx={{
                minHeight: "48px",
                borderRadius: "999px",
                border: "1px solid color-mix(in srgb, var(--logoColor) 40%, transparent)",
                backgroundColor: "transparent",
                color: "var(--logoColor2)",
                fontFamily: "Poppins, sans-serif",
                fontWeight: 700,
                fontSize: "15px",
                cursor: "pointer",
                "&:hover": { borderColor: "var(--logoColor2)" },
                "&:focus-visible": { outline: "2px solid var(--logoColor)", outlineOffset: "3px" },
              }}
            >
              Sign in to The Branches
            </BranchesSignInButton>
            <Typography sx={{ ...text, fontSize: "12px", lineHeight: 1.55, opacity: 0.65 }}>
              One email to confirm, then new music when it lands. Every email has a one click way out.
            </Typography>
          </>
        )}
      </Box>
    </BaseModal>
  );
}

export default UnlockArcadeDialog;
